/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React, { useCallback, useState } from 'react'
import { capitalize } from 'lodash'

import { EuiFlexGroup, EuiFlexItem, EuiRadio } from '@elastic/eui'

import type { BillingSubscriptionLevel } from '@modules/ui-types'
import { SUBSCRIPTION_LEVELS } from '@modules/billing-lib/constants'

import ConfirmModal from './ConfirmModal'
import PendingLevelNotice from './PendingLevelNotice'

interface Props {
  currentLevel: BillingSubscriptionLevel
  organizationId: string
  pendingLevelValidFrom?: string
  pendingLevel?: BillingSubscriptionLevel
}

const SubscriptionChangeRadios = ({
  currentLevel,
  organizationId,
  pendingLevel,
  pendingLevelValidFrom,
}: Props) => {
  const [selectedLevel, setSelectedLevel] = useState<BillingSubscriptionLevel>(currentLevel)
  const [openConfirmModal, setOpenModal] = useState<boolean>(false)

  const handleCloseModal = useCallback(() => {
    setOpenModal(false)
    setSelectedLevel(currentLevel)
  }, [currentLevel])

  return (
    <EuiFlexGroup direction='column' gutterSize='m'>
      <PendingLevelNotice
        currentLevel={currentLevel}
        pendingLevel={pendingLevel}
        pendingLevelValidFrom={pendingLevelValidFrom}
      />
      <EuiFlexGroup gutterSize='m' alignItems='center'>
        {SUBSCRIPTION_LEVELS.map((level) => (
          <EuiFlexItem key={level} grow={false}>
            <EuiRadio
              name='subscriptionLevel'
              data-test-id={`subscription-change-button--${level}`}
              id={level}
              label={capitalize(level)}
              checked={currentLevel === level}
              onChange={() => {
                setOpenModal(true)
                setSelectedLevel(level)
              }}
            />
          </EuiFlexItem>
        ))}
        {openConfirmModal && (
          <ConfirmModal
            onClose={handleCloseModal}
            organizationId={organizationId}
            selectedLevel={selectedLevel}
          />
        )}
      </EuiFlexGroup>
    </EuiFlexGroup>
  )
}

export default SubscriptionChangeRadios
